import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Users, Maximize2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { ImageGallery } from '../components/ImageGallery';
import { BookingWidget } from '../components/hotelDetails/BookingWidget';
import { roomImageFor } from '../utils/roomImages';
import { RatingBadge } from '../components/RatingBadge';
import { ratingLabel } from '../utils/rating10';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = 'https://images.unsplash.com/photo-1561501900-3701fa6a0864?w=1200';

export const HotelDetailsPage = () => {
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState(() => {
    // Initialise from URL search params so dates/guests selected on /home autofill here.
    const ci = searchParams.get('check_in') || '';
    const co = searchParams.get('check_out') || '';
    const adults = parseInt(searchParams.get('adults') || '2', 10);
    const children = parseInt(searchParams.get('children') || '2', 10);
    return {
      room_type_id: '',
      check_in: ci,
      check_out: co,
      adults: Number.isNaN(adults) ? 2 : Math.max(1, adults),
      children: Number.isNaN(children) ? 2 : Math.max(0, children),
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      booking_type: 'full_day',
      slots: [],
      slot_total_amount: 0,
    };
  });
  const [booking, setBooking] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [guestCode, setGuestCode] = useState('');
  const [walletInfo, setWalletInfo] = useState(null);  // {balance, owner_name}
  const [redeemAmount, setRedeemAmount] = useState(0);
  const [reviewFilter, setReviewFilter] = useState(0);  // 0 = all, 5/4/3/2/1
  const [showFilters, setShowFilters] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const validateCode = useCallback(async () => {
    if (!guestCode || guestCode.length !== 9) {
      setWalletInfo(null);
      setRedeemAmount(0);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/guest/wallet/validate-code`, { guest_code: guestCode });
      setWalletInfo(data);
      if (data.balance === 0) toast.info('This wallet has ₹0 balance');
    } catch (error) {
      setWalletInfo(null);
      setRedeemAmount(0);
      toast.error(error.response?.data?.detail || 'Invalid guest code');
    }
  }, [guestCode]);

  const fetchHotelDetails = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/guest/hotels/${hotelId}`);
      setHotel(data);
      if (data.room_types && data.room_types.length > 0) {
        // Quick-book picks cheapest room; default picks first
        const isQuick = searchParams.get('quick') === '1';
        const target = isQuick
          ? [...data.room_types].sort((a, b) => (a.base_price || 0) - (b.base_price || 0))[0]
          : data.room_types[0];
        setBookingData((prev) => ({ ...prev, room_type_id: target._id }));
      }
    } catch (error) {
      toast.error('Failed to load hotel details');
    } finally {
      setLoading(false);
    }
  }, [hotelId, searchParams]);

  useEffect(() => {
    fetchHotelDetails();
  }, [fetchHotelDetails]);

  // === Quick-book auto-scroll to booking widget ===
  useEffect(() => {
    if (!loading && hotel && searchParams.get('quick') === '1') {
      // small delay so layout settles, then scroll widget into view
      const t = setTimeout(() => {
        const el = document.querySelector('[data-testid="booking-widget"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [loading, hotel, searchParams]);

  // === Share-token "open" event — credit ₹10 to sharer on first unique open ===
  useEffect(() => {
    const st = searchParams.get('st');
    if (!st) return;
    const seenKey = `localliink_st_seen_${st}`;
    if (sessionStorage.getItem(seenKey)) return;
    sessionStorage.setItem(seenKey, '1');
    axios.get(`${API}/rewards/share/open`, { params: { token: st } }).catch(() => { /* silent */ });
  }, [searchParams]);

  // === Capture referrer guest_code from URL ?ref=XXX → localStorage (used at booking) ===
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && ref.length >= 4) {
      localStorage.setItem('localliink_referrer', ref.toUpperCase());
    }
  }, [searchParams]);

  // === Share via WhatsApp — mints a unique share_token then opens WhatsApp ===
  const openWhatsApp = (text) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    // Use a real anchor click so mobile WhatsApp deep-link triggers reliably
    // and the popup blocker doesn't kill the navigation.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleWhatsAppShare = async () => {
    const plain = window.location.origin + `/hotels/${hotelId}`;
    if (!user || user.role !== 'guest') {
      // anonymous / non-guest fallback — just share the plain URL
      const text = `${hotel?.hotel_name || 'Check out this hotel'} on localliink.com — ${plain}`;
      openWhatsApp(text);
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/rewards/share/create`,
        { hotel_id: hotelId },
        { withCredentials: true }
      );
      const shareUrl = `${window.location.origin}/hotels/${hotelId}?st=${data.token}`;
      const text = `${hotel?.hotel_name || 'Check this out'} on localliink.com — ${shareUrl}`;
      openWhatsApp(text);
      toast.success('Earn ₹10 when someone opens your shared link!');
    } catch (e) {
      // graceful fallback — still send the plain link so the share never silently fails
      const text = `${hotel?.hotel_name || 'Check out this hotel'} on localliink.com — ${plain}`;
      openWhatsApp(text);
      toast.error('Share link minted as plain URL (couldn\'t create reward token)');
    }
  };

  const nights =
    bookingData.check_in && bookingData.check_out
      ? Math.max(1, (new Date(bookingData.check_out) - new Date(bookingData.check_in)) / (1000 * 60 * 60 * 24))
      : 1;
  const selectedRoom = hotel?.room_types?.find((r) => r._id === bookingData.room_type_id);
  const isHourly = bookingData.booking_type === 'hourly';
  // Hourly mode: total = sum of selected slot prices (slot_total_amount stored by HourlySlotPicker)
  // Full-day mode: total = base_price * nights
  const grossTotal = isHourly
    ? Number(bookingData.slot_total_amount || 0)
    : (selectedRoom ? selectedRoom.base_price * nights : 0);
  const appliedRedeem = Math.min(redeemAmount, walletInfo?.balance || 0, grossTotal);
  const total = Math.max(0, grossTotal - appliedRedeem);

  const handleShare = async () => {
    const url = window.location.href;
    const title = hotel?.hotel_name ? `${hotel.hotel_name} on localliink.com` : 'localliink.com';
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch (err) { /* user cancelled share dialog — non-fatal */ console.debug('share cancelled', err); }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    const isHourly = bookingData.booking_type === 'hourly';
    if (isHourly) {
      if (!bookingData.check_in) {
        toast.error('Please pick a date'); return;
      }
      if (!Array.isArray(bookingData.slots) || bookingData.slots.length === 0) {
        toast.error('Please pick at least one slot'); return;
      }
    } else {
      if (!bookingData.check_in || !bookingData.check_out) {
        toast.error('Please select check-in and check-out dates');
        return;
      }
      if (bookingData.check_out <= bookingData.check_in) {
        toast.error('Check-out must be after check-in');
        return;
      }
    }
    if (authLoading) {
      toast.info('Please wait...');
      return;
    }
    if (!user) {
      toast.error('Please login to book');
      navigate('/login');
      return;
    }
    if (user.role !== 'guest') {
      toast.error('Only guests can make bookings');
      return;
    }

    setBooking(true);
    try {
      const totalGuests = (bookingData.adults || 0) + (bookingData.children || 0);
      const payload = { ...bookingData, guests: totalGuests, hotel_id: hotelId };
      // strip client-only helper fields
      delete payload.slot_total_amount;
      if (bookingData.booking_type === 'hourly') {
        // single-date booking
        payload.check_out = bookingData.check_in;
      }
      if (guestCode && appliedRedeem > 0) {
        payload.guest_code = guestCode;
        payload.redeem_amount = appliedRedeem;
      }
      // attach referrer code captured from URL (?ref=...) — server credits ₹50 to referrer
      const refCode = localStorage.getItem('localliink_referrer');
      if (refCode && refCode !== (user?.guest_code || '')) {
        payload.referrer_guest_code = refCode;
      }
      const { data } = await axios.post(
        `${API}/guest/bookings`,
        payload,
        { withCredentials: true }
      );
      toast.success(data.message);
      // clear consumed referrer so subsequent bookings don't double-attribute
      if (refCode) localStorage.removeItem('localliink_referrer');
      navigate('/my-bookings');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Booking failed';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF0059]"></div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 text-center">
        <p className="text-gray-600">Hotel not found</p>
      </div>
    );
  }

  const images = (hotel.images && hotel.images.length > 0 ? hotel.images : [FALLBACK]);
  const main = images[0];
  const small = images.slice(1, 5);
  while (small.length < 4) small.push(FALLBACK);

  return (
    <div className="min-h-screen pb-16 bg-white" data-testid="hotel-details-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Title block */}
        <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-black">{hotel.hotel_name}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2">
              {hotel.rating > 0 && (
                <RatingBadge value={hotel.rating} count={hotel.rating_count} size="md" testId="hotel-detail-rating" />
              )}
              <span className="text-gray-300">·</span>
              <p className="text-sm text-gray-700 flex items-center gap-1">
                <MapPin className="h-4 w-4 text-[#FF0059]" /> {hotel.hotel_address}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleWhatsAppShare}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-bold transition-colors"
              data-testid="share-whatsapp-button"
              title="Share via WhatsApp — earn ₹10 when someone opens your link"
            >
              <Share2 className="h-4 w-4" /> WhatsApp
            </button>
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-300 hover:border-[#FF0059] hover:bg-[#FFE5EE] text-sm font-bold text-black transition-colors"
              data-testid="share-hotel-button"
            >
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>
        </div>

        {/* Mosaic image gallery */}
        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 md:h-[480px] rounded-2xl overflow-hidden relative" data-testid="hotel-mosaic">
          {/* Badges overlay — top-left corner of the gallery */}
          {Array.isArray(hotel.badges) && hotel.badges.length > 0 && (
            <div className="absolute top-4 left-4 z-20 flex flex-wrap gap-1.5 max-w-[60%]" data-testid="gallery-badges">
              {hotel.badges.slice(0, 4).map((b, i) => (
                <span
                  key={b}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider backdrop-blur-sm shadow-md"
                  style={{
                    background: i % 2 === 0 ? 'rgba(255, 0, 89, 0.95)' : 'rgba(255, 203, 0, 0.95)',
                    color: i % 2 === 0 ? '#fff' : '#000',
                  }}
                  data-testid={`gallery-badge-${i}`}
                >
                  {b}
                </span>
              ))}
            </div>
          )}
          <button
            onClick={() => setGalleryOpen(true)}
            className="md:col-span-2 md:row-span-2 relative overflow-hidden group h-64 md:h-auto"
            data-testid="mosaic-main"
          >
            <img src={main} alt={hotel.hotel_name} className="w-full h-full object-cover group-hover:opacity-95 transition-opacity" />
          </button>
          {small.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setGalleryOpen(true)}
              className="hidden md:block relative overflow-hidden group"
              data-testid={`mosaic-small-${idx}`}
            >
              <img src={img} alt={`${hotel.hotel_name} ${idx + 2}`} className="w-full h-full object-cover group-hover:opacity-95 transition-opacity" />
            </button>
          ))}
          {images.length > 0 && (
            <button
              onClick={() => setGalleryOpen(true)}
              className="absolute bottom-4 right-4 bg-white text-black font-bold px-4 py-2 rounded-lg border-2 border-black flex items-center gap-2 hover:bg-black hover:text-white transition-colors shadow-lg"
              data-testid="see-all-photos"
            >
              <Maximize2 className="h-4 w-4" />
              See all {images.length} photos
            </button>
          )}
        </div>

        {/* Tagline */}
        {hotel.marketing_tagline && (
          <p className="mt-6 text-xl italic text-[#FF0059] font-bold">"{hotel.marketing_tagline}"</p>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT — Rooms & content */}
          <div className="lg:col-span-2 space-y-10">
            {/* About */}
            {hotel.description && (
              <div data-testid="about-section">
                <h2 className="text-2xl font-black text-black mb-3">About this stay</h2>
                <p className="text-base text-gray-700 leading-relaxed">{hotel.description}</p>
              </div>
            )}

            {/* Amenities */}
            {hotel.amenities && hotel.amenities.length > 0 && (
              <div data-testid="amenities-section">
                <h2 className="text-2xl font-black text-black mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {hotel.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FAFAFA] border border-gray-200">
                      <span className="w-2 h-2 rounded-full bg-[#FF0059]" />
                      <span className="text-sm font-semibold text-black">{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Check-in / Check-out times */}
            {(hotel.check_in_time || hotel.check_out_time) && (
              <div className="flex flex-wrap gap-3" data-testid="checkin-times">
                <div className="flex-1 min-w-[180px] flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#FFF5F8] border border-[#FFE5EE]">
                  <div className="w-9 h-9 rounded-full bg-[#FF0059] text-white flex items-center justify-center font-black text-xs">IN</div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Check-in</p>
                    <p className="text-base font-black text-black" data-testid="hotel-checkin-time">{hotel.check_in_time || '14:00'}</p>
                  </div>
                </div>
                <div className="flex-1 min-w-[180px] flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#FFFDF2] border border-[#FFCB00]/30">
                  <div className="w-9 h-9 rounded-full bg-[#FFCB00] text-black flex items-center justify-center font-black text-xs">OUT</div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Check-out</p>
                    <p className="text-base font-black text-black" data-testid="hotel-checkout-time">{hotel.check_out_time || '11:00'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Rooms */}
            <div data-testid="rooms-section">
              <h2 className="text-2xl font-black text-black mb-4">Choose your room</h2>
              {hotel.room_types && hotel.room_types.length > 0 ? (
                <div className="space-y-4">
                  {hotel.room_types.map((room) => {
                    const roomImage = roomImageFor(room, hotel);
                    return (
                    <div
                      key={room._id}
                      onClick={() => setBookingData({ ...bookingData, room_type_id: room._id })}
                      className={`rounded-2xl border-2 cursor-pointer transition-all overflow-hidden ${
                        bookingData.room_type_id === room._id
                          ? 'border-[#FF0059] bg-[#FFE5EE]/40'
                          : 'border-gray-200 hover:border-[#FF0059]/50'
                      }`}
                      data-testid={`room-type-${room._id}`}
                    >
                      <div className="flex flex-col sm:flex-row">
                        <div className="sm:w-56 h-44 sm:h-auto bg-gray-100 flex-shrink-0 overflow-hidden">
                          <img
                            src={roomImage}
                            alt={room.name}
                            className="w-full h-full object-cover"
                            data-testid={`room-image-${room._id}`}
                          />
                        </div>
                        <div className="flex-1 p-5 flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-black text-black">{room.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">{room.description}</p>
                            <div className="flex items-center gap-3 mt-3 text-xs text-gray-600">
                              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Up to {room.capacity} guests</span>
                            </div>
                            {room.amenities && room.amenities.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-3">
                                {room.amenities.map((a) => (
                                  <span key={a} className="px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-600 bg-gray-100 rounded">
                                    {a}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xl font-black text-black">₹{room.base_price.toLocaleString('en-IN')}</p>
                            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">per night</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-600">No rooms available at the moment.</p>
              )}
            </div>

            {/* Nearby Attractions */}
            {hotel.nearby_attractions && Object.keys(hotel.nearby_attractions).length > 0 && (
              <div data-testid="attractions-section">
                <h2 className="text-2xl font-black text-black mb-4">Around the property</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(hotel.nearby_attractions).map(([key, items]) => (
                    Array.isArray(items) && items.length > 0 && (
                      <div key={key} className="p-4 rounded-xl bg-[#FAFAFA] border border-gray-200">
                        <h4 className="font-bold text-sm text-black uppercase tracking-wider mb-2 capitalize">{key}</h4>
                        <ul className="space-y-1">
                          {items.slice(0, 5).map((item, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                              <MapPin className="h-3 w-3 text-[#FF0059] mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Reviews */}
            {hotel.reviews && hotel.reviews.length > 0 && (
              <div data-testid="reviews-section">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-black text-black flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-[#FF0059] text-white text-xs font-black">/10</span>
                    Guest reviews ({hotel.reviews.length})
                  </h2>
                  <button
                    onClick={() => setShowFilters((v) => !v)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 hover:border-[#FF0059] text-xs font-bold text-black transition-colors"
                    data-testid="toggle-review-filters"
                  >
                    {showFilters ? 'Hide filters' : 'Filter reviews'}
                  </button>
                </div>
                {showFilters && (
                  <div className="flex flex-wrap gap-2 mb-4" data-testid="review-filter-chips">
                    {[0, 9, 7, 5, 3].map((r) => (
                      <button
                        key={r}
                        onClick={() => setReviewFilter(r)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                          reviewFilter === r
                            ? 'bg-[#FF0059] text-white border-[#FF0059]'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#FF0059]'
                        }`}
                        data-testid={`filter-rating-${r}`}
                      >
                        {r === 0 ? 'All ratings' : `${r}+ only`}
                      </button>
                    ))}
                  </div>
                )}
                <div className="space-y-3">
                  {hotel.reviews
                    .filter((r) => (reviewFilter === 0 ? true : r.rating >= reviewFilter))
                    .map((review) => (
                      <div key={review._id} className="bg-[#FAFAFA] border border-gray-200 rounded-2xl p-5" data-testid={`review-${review._id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <RatingBadge value={review.rating} size="sm" showLabel={false} testId={`review-rating-${review._id}`} />
                          <span className="text-sm font-bold text-black">{ratingLabel(review.rating)}</span>
                        </div>
                        {review.comment && <p className="text-gray-700 text-sm leading-relaxed">{review.comment}</p>}
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(review.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                        {review.hotel_reply && (
                          <div className="mt-3 pl-4 border-l-2 border-[#FF0059] bg-[#FFE5EE]/40 rounded-r-lg p-3" data-testid={`hotel-reply-${review._id}`}>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#FF0059] mb-1">Reply from {hotel.hotel_name}</p>
                            <p className="text-sm text-gray-700">{review.hotel_reply}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  {hotel.reviews.filter((r) => (reviewFilter === 0 ? true : r.rating >= reviewFilter)).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-6">No reviews with {reviewFilter}+ rating.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — Booking widget */}
          <div className="lg:col-span-1">
            <BookingWidget
              hotel={hotel}
              bookingData={bookingData}
              setBookingData={setBookingData}
              selectedRoom={selectedRoom}
              nights={nights}
              grossTotal={grossTotal}
              appliedRedeem={appliedRedeem}
              total={total}
              booking={booking}
              handleBooking={handleBooking}
              guestCode={guestCode}
              setGuestCode={setGuestCode}
              walletInfo={walletInfo}
              redeemAmount={redeemAmount}
              setRedeemAmount={setRedeemAmount}
              validateCode={validateCode}
            />
          </div>
        </div>
      </div>

      <ImageGallery
        images={images}
        hotelName={hotel.hotel_name}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />
    </div>
  );
};
